import { ChangeEvent as ReactChangeEvent, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import { APP_ENV_CONFIG } from '../config';
import { PROFILE } from './Profile/IDs';
import { CanadianProvince, Profile as ProfileType, USState } from '../../common/Types';
import {
  Center,
  Controls,
  Footer,
  FooterControls,
  Header,
  Layout,
  Left,
  MainLayout,
  Right,
} from '../Components/Layout';
import { uploadFileToS3SignedUrl } from '../Utils/FileUtils';
import styled from 'styled-components';
import { PositionID } from '../../common/Positions/Constants';

const getDataOptionsForObject = <ObjectType extends Record<any, any>>(obj: ObjectType): ReactNode[] =>
  Object.keys(obj).map((k) => (
    <option key={k} value={obj[k]}>
      {obj[k]}
    </option>
  ));

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const ProfileContainer = styled.div<{ modified?: boolean }>`
  background-color: ${({ modified }) => (modified ? 'var(--pico-primary-focus)' : 'unset')};
  padding: 1em;
  border-radius: 0.5em;
`;

export const useProfileRPCs = ({ authorization }: { authorization: string }) => {
  const profileRPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/profile',
        authorization,
      },
      path: '/read',
    }),
    [authorization]
  );
  const profileSaveRPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/profile',
        authorization,
      },
      path: '/save',
    }),
    [authorization]
  );

  return { profileRPC, profileSaveRPC };
};

export type ProfileProps = {
  authorization: string;
  initialPositionID: PositionID | undefined;
};

export const Profile: FC<ProfileProps> = ({ authorization, initialPositionID }) => {
  const [initialPositionIDSaved, setInitialPositionIDSaved] = useState<boolean>(false);
  const { profileRPC, profileSaveRPC } = useProfileRPCs({ authorization });
  const [editingStateInput, setEditingStateInput] = useState<boolean>(false);
  const onStateInputFocus = useCallback(() => setEditingStateInput(true), []);
  const onStateInputBlur = useCallback(() => setEditingStateInput(false), []);
  const {
    modified: profileModified,
    value: profile = {},
    onChange: onProfileChange,
  } = useApplicationStateValue(PROFILE.READ);
  const { complete, linkedInUrl, city, state, idCode }: ProfileType = profile;
  const { loading: readLoading, invalidate: invalidateProfileInternal } = useApplicationStateLoader({
    identifier: PROFILE.READ,
    remoteProcedureCall: profileRPC,
  });
  const invalidateProfile = useCallback(() => {
    invalidateProfileInternal();
  }, [invalidateProfileInternal]);
  const { loading: saveLoading, makeRemoteProcedureCall: saveProfile } = useApplicationStateLoader({
    identifier: PROFILE.SAVE,
    remoteProcedureCall: profileSaveRPC,
    manual: true,
    onLoadComplete: invalidateProfile,
  });
  const onLinkedInInputChange = useCallback(
    ({ target: { value: newLinkedInUrl = '' } }: ReactChangeEvent<HTMLInputElement>) => {
      onProfileChange({
        ...profile,
        linkedInUrl: newLinkedInUrl,
      });
    },
    [profile, onProfileChange]
  );
  const onCityInputChange = useCallback(
    ({ target: { value: newCity = '' } }: ReactChangeEvent<HTMLInputElement>) => {
      onProfileChange({
        ...profile,
        city: newCity,
      });
    },
    [profile, onProfileChange]
  );
  const onStateInputChange = useCallback(
    ({ target: { value: newState = '' } }: ReactChangeEvent<HTMLInputElement>) => {
      onProfileChange({
        ...profile,
        state: newState,
      });
    },
    [profile, onProfileChange]
  );
  const onIDCodeInputChange = useCallback(
    ({ target: { value: newIDCode = '' } }: ReactChangeEvent<HTMLInputElement>) => {
      onProfileChange({
        ...profile,
        idCode: newIDCode,
      });
    },
    [profile, onProfileChange]
  );
  const onBeginInterviewClick = useCallback(() => {
    history.pushState({}, '', '/interview');
  }, []);
  const resumeUploadUrlRPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/file-urls',
        authorization,
      },
      path: '/upload-resume',
    }),
    [authorization]
  );
  const [resumeUploadFailed, setResumeUploadFailed] = useState(false);
  const [resumeFileIsUploading, setResumeFileIsUploading] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | undefined>();
  const { value: resumeUploadUrl = '' } = useApplicationStateValue(PROFILE.RESUME_UPLOAD_URL);
  const { loading: resumeUploadUrlLoading, makeRemoteProcedureCall: getResumeUploadUrl } = useApplicationStateLoader({
    identifier: PROFILE.RESUME_UPLOAD_URL,
    remoteProcedureCall: resumeUploadUrlRPC,
    manual: true,
  });
  const uploadResume = useCallback(async () => {
    if (resumeFile && resumeUploadUrl) {
      setResumeFileIsUploading(true);
      setResumeUploadFailed(false);

      try {
        await uploadFileToS3SignedUrl(resumeUploadUrl, resumeFile);
      } catch (error) {
        setResumeUploadFailed(true);
      }

      invalidateProfile();
      setResumeFileIsUploading(false);
    }
  }, [resumeFile, resumeUploadUrl, invalidateProfile]);
  const onFileChosen = useCallback(
    ({ target: { files } }: ReactChangeEvent<HTMLInputElement>) => {
      if (files) {
        const file = files[0];

        if (file) {
          setResumeFile(file);
        }
      }
    },
    [profile, onProfileChange]
  );
  const isLoading = readLoading || saveLoading || resumeUploadUrlLoading || resumeFileIsUploading;

  useEffect(() => {
    if (!initialPositionIDSaved && typeof initialPositionID !== 'undefined' && initialPositionID in PositionID) {
      setInitialPositionIDSaved(true);
      saveProfile({
        positionID: initialPositionID as PositionID,
      }).then(invalidateProfile);
    }
  }, [initialPositionIDSaved, initialPositionID, saveProfile, invalidateProfile]);

  useEffect(() => {
    if (!editingStateInput && profileModified) {
      const sTid = setTimeout(() => {
        saveProfile(profile);
      }, 1000);

      return () => clearTimeout(sTid);
    }
  }, [profileModified, profile, saveProfile, editingStateInput]);

  useEffect(() => {
    if (resumeFile && !resumeUploadUrl) {
      getResumeUploadUrl();
    } else if (resumeFile && resumeUploadUrl) {
      uploadResume();
    }
  }, [resumeFile, resumeUploadUrl, getResumeUploadUrl, uploadResume]);

  return (
    <Layout>
      <Header>
        <h2>Profile</h2>
        {isLoading ? <span aria-busy="true">Loading...</span> : <p>{complete ? '' : '(Please Complete)'}</p>}
      </Header>
      <MainLayout>
        <Left />
        <Center>
          <ProfileContainer modified={profileModified}>
            <label>
              LinkedIn URL:
              <input type="text" value={linkedInUrl} onChange={onLinkedInInputChange} />
            </label>
            <label>
              City:
              <input type="text" value={city} onChange={onCityInputChange} />
            </label>
            <label>
              State / Province:
              <input
                list="profile-state-province"
                type="text"
                value={state}
                onChange={onStateInputChange}
                onFocus={onStateInputFocus}
                onBlur={onStateInputBlur}
              />
              <datalist id="profile-state-province">
                {getDataOptionsForObject(USState)}
                {getDataOptionsForObject(CanadianProvince)}
              </datalist>
            </label>
            <label>
              ID Code:
              <input type="text" value={idCode} onChange={onIDCodeInputChange} />
            </label>
            <Controls>
              <label>
                Upload Resume:
                {resumeUploadFailed ? ' (Failed: Please try again!)' : complete ? ' (Resume has been uploaded.)' : ''}
                <input type="file" onChange={onFileChosen} aria-invalid={resumeUploadFailed ? true : undefined} />
              </label>
            </Controls>
          </ProfileContainer>
        </Center>
        <Right />
      </MainLayout>
      <Footer>
        <FooterControls>
          {editingStateInput ? <button>Continue</button> : undefined}
          <button disabled={!complete || isLoading} onClick={onBeginInterviewClick}>
            Begin Interview
          </button>
        </FooterControls>
      </Footer>
    </Layout>
  );
};
